import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import type { ValidationQuickFixTransaction } from '../../../shared/stores/usePcfValidationStore';
import {
  mapRevitOperationSnapshotToProgressModel,
  type OperationProgressModel,
} from '../../../shared/ui/progress';

export const mapQuickFixUndoProgress = (
  progress: RevitOperationProgressSnapshot
): OperationProgressModel =>
  mapRevitOperationSnapshotToProgressModel(progress, {
    title: 'Undo quick fix',
    message: 'Restoring previous validation values in Revit.',
    completeMessage: 'Quick fix undo completed.',
    errorMessage: 'Quick fix undo failed.',
    locked: true,
  });

export interface ValidationRepairActionRunner {
  onRunValidationScoped?: (validationIds: number[]) => Promise<void> | void;
}

export async function runValidationAfterRepair(
  validationIds: number[],
  runner: ValidationRepairActionRunner
): Promise<void> {
  const affectedValidationIds = Array.from(new Set(validationIds));
  if (!runner.onRunValidationScoped || affectedValidationIds.length === 0) {
    return;
  }

  await runner.onRunValidationScoped(affectedValidationIds);
}

export interface UndoQuickFixActionDeps {
  transaction: ValidationQuickFixTransaction;
  undoQuickFixTransaction: (
    transaction: ValidationQuickFixTransaction,
    options: { onProgress: (progress: RevitOperationProgressSnapshot) => void }
  ) => Promise<{ affectedValidationIds: number[] }>;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  setUndoingQuickFix: (undoing: boolean) => void;
  setUndoProgress: (progress: OperationProgressModel | null) => void;
  onAfterRepair: (validationIds: number[]) => Promise<void> | void;
}

export function buildUndoQuickFixAction(deps: UndoQuickFixActionDeps): {
  label: string;
  tone: 'secondary';
  onClick: () => Promise<void>;
} {
  return {
    label: 'Undo last quick fix',
    tone: 'secondary',
    onClick: async () => {
      deps.setUndoingQuickFix(true);
      deps.setUndoProgress(null);
      try {
        const result = await deps.undoQuickFixTransaction(deps.transaction, {
          onProgress: (progress) => deps.setUndoProgress(mapQuickFixUndoProgress(progress)),
        });
        deps.showSuccess('Quick fix was undone.');
        await deps.onAfterRepair(result.affectedValidationIds);
      } catch (error: any) {
        deps.showError(error?.message || 'Failed to undo quick fix');
      } finally {
        deps.setUndoingQuickFix(false);
        deps.setUndoProgress(null);
      }
    },
  };
}
